import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
// WEB-1955: «отказ без пейлоада» больше не собирается руками из
// `action.error.message` — текстом терялся код, и экран кошелька показывал
// общее «Unknown error» вместо причины. Разбор — одной дверью.
import { rejectedApiError } from './rejectedApiError';
import { billingApi } from '@/api/billing';
import type {
  BillingResponse,
  BillingTier,
  SubscriptionSummary,
  TierLimits,
  TierUsage,
  TransactionRow,
  BillingPrompt,
  CreditsPurchaseRequest,
  CreditsPurchaseError,
  CheckoutResponse,
  PortalResponse,
  TransactionListResponse,
  AiCostCatalogResponse,
  BillingPlansResponse,
  WalletLot,
  PlanTerms,
  TrialOffer,
  TrialProgress,
  OrgAccessState,
  CheckoutPeriod,
} from '@/types/billing';
import type { TransactionQueryParams } from '@/api/billing';
import {
  IMAGE_CAP_FLOOR_KEY,
  QUIZ_SCREEN_COUNT_MAX_KEY,
  PAYWALL_SCREEN_COUNT_MAX_KEY,
} from '@/utils/generationEstimate';
import {
  EDIT_TURN_METERED_ESTIMATE_KEY,
  EDIT_TURN_METERED_ESTIMATE_WITH_REFERENCE_KEY,
} from '@/utils/editTurnPricing';
import { broadcastBalanceSync } from '@/utils/billingSync';
// WEB-1518: общее правило класса «сбой проверки ≠ отрицательный ответ».
import { checkStateFromError, type CheckState } from '@/utils/checkOutcome';

export interface BillingState {
  subscription: SubscriptionSummary | null;
  /**
   * WEB-1290: `BillingTier`, not `SubscriptionTier` — the store must be able
   * to represent "we don't know" ({@link BillingTier}'s `'UNKNOWN'`)
   * separately from the confirmed-free `'FREE'`. See the type doc for why.
   */
  tier: BillingTier;
  limits: TierLimits | null;
  usage: TierUsage | null;
  transactions: TransactionRow[];
  transactionsTotal: number;
  isLoadingBilling: boolean;
  isLoadingTransactions: boolean;
  isLoadingCheckout: boolean;
  isLoadingTopUp: boolean;
  /**
   * WEB-1954: отказ последнего запроса — РАЗОБРАННЫЙ (`status`, `message`,
   * `code`, `reasonCode`), а не голый текст. Голый текст убивал код: читатель
   * этого поля по построению не смог бы спросить реестр кодов и показал бы
   * человеку язык сервера, а не язык кабинета (правило WEB-1874).
   */
  error: ApiErrorInfo | null;
  redirectUrl: string | null;
  billingPrompt: BillingPrompt | null;
  /** Prepaid wallet balance in cents (funds domain purchases). */
  balanceCents: number;
  /**
   * WEB-2826: свободный остаток и резерв идущих действий ИИ — как их прислал
   * сервер (WEB-2837); `null` — ответ без полей (старый сервер) или ещё не
   * загружено. Напрямую не читать: только `selectWalletAvailableCents` /
   * `selectWalletHeldCents` (одна дверь utils/walletAvailable.ts — она же
   * переживает подмены среза в тестах, которые не знают новых выходов).
   */
  availableCents: number | null;
  heldCents: number | null;
  /**
   * РЕВИЗИЯ ПАЧЕК 6-7 (пункт 4) — долг организации, как его назвал СЕРВЕР.
   * Остаток кошелька отрицательным не бывает (считается по живым лотам), долг
   * живёт в журнале проводок — поэтому признак блокировки отдельным полем, а
   * не выводом `balanceCents < 0` (заведомо ложным). `debtCents` — глубина
   * долга положительным числом. См. types/billing.ts BillingResponse.
   */
  debtBlocked: boolean;
  debtCents: number;
  /**
   * WEB-665 (round 3): optional lot breakdown of `balanceCents`. Null until
   * a response carries the field (backend atom pending — optional on the
   * response type, see types/billing.ts WalletLot doc).
   */
  lots: WalletLot[] | null;
  /**
   * WEB-1487×WEB-1524 (ревизия пачки 1, FA7): orgId, для которой был сделан
   * запрос, снявший текущий `subscription`. WEB-1337 P2 (см. коммент на
   * fetchBillingThunk.rejected ниже) намеренно НЕ обнуляет subscription на
   * pending/rejected — это правильно для транзиентного сбоя ТОЙ ЖЕ орги, но
   * OrgSwitcher.tsx:110-124 диспатчит clearActiveProjectThunk/
   * fetchProjectsThunk/fetchBillingThunk не дожидаясь ответов, так что
   * activeOrgId и список проектов могут уже быть орги Б, пока subscription
   * ещё держит снимок орги А. `null` только до первой загрузки (тот же смысл,
   * что и `subscription === null`) — потребители (PastDueBanner,
   * CurrentPlanCard PAST_DUE-информер) обязаны прятать данные о просрочке,
   * когда `subscriptionOrgId !== organizations.activeOrgId` (обе стороны не
   * null), а не доверять staleness вслепую.
   */
  subscriptionOrgId: string | null;
  /**
   * True после первого успешного (или проваленного) fetchBillingThunk. Нужен
   * потребителям (HeaderBalanceWidget) чтобы показать skeleton до первой
   * загрузки: balanceCents=0 сам по себе неоднозначен («ноль» vs «не загружено»).
   */
  billingLoaded: boolean;
  /**
   * WEB-2826 (добор приёмки): кошелёк хоть раз пришёл с сервера. Ставится
   * ТОЛЬКО удачной загрузкой. `billingLoaded` true и после упавшей первой
   * загрузки (WEB-1337), а числа там — начальный 0, то есть «не знаем»; без
   * этого признака дверь utils/walletAvailable.ts отдала бы «доступно 0».
   */
  walletConfirmed: boolean;
  /** AI operation cost catalog: operationType → creditsRequired. Null until fetched. */
  costCatalog: Record<string, number> | null;
  isLoadingCostCatalog: boolean;
  /**
   * WEB-3911: номер последнего запроса каталога (`meta.requestId` thunk'а).
   * Ложится ответ только этого запроса: ответ для прежней организации,
   * пришедший после смены (сброс стирает номер), в стор не попадает.
   */
  costCatalogRequestId: string | null;
  /**
   * WEB-3911: в этом контексте (организация/проект) каталог уже просили.
   * `useTier` перечитывает каталог, когда он пуст, не грузится и попытки
   * ещё не было — отказ сервера не даёт бесконечного перезапроса; сброс
   * при смене контекста (`resetCentralSlices`) снимает признак.
   */
  costCatalogAttempted: boolean;
  /**
   * WEB-611 (money HIGH): live Pro price read from Stripe. Null
   * until fetched — consumers (PricingModal) must show a loading state
   * rather than fall back to any hardcoded number while this is null.
   */
  planPrices: BillingPlansResponse | null;
  isLoadingPlanPrices: boolean;
  /**
   * WEB-1094 (деньги): заранее данное разрешение владельца «оплачивать
   * тариф с баланса, если карта не прошла». Default false.
   */
  payFromBalanceOnCardFailEnabled: boolean;
  /**
   * WEB-1094 (деньги): «списание с карты не прошло, тариф оплачен с
   * баланса» — персистентная плашка (не одноразовый ack).
   */
  paidFromBalanceBanner: boolean;
  /**
   * QA-возврат 25.08 (WEB-1126 + WEB-1290): сервер явно сказал, что платёжная
   * часть скрыта настройками доступа. Только для текста заглушек — блокировки
   * по-прежнему решаются тарифом (см. BillingResponse.billingHidden).
   * Ответ без признака (старый сервер) = false, а не «скрыто».
   */
  billingHidden: boolean;
  /**
   * WEB-4008: условия тарифа и ход пробного — как их прислал сервер; `null` —
   * нет в ответе (маска прав, старый сервер) или ещё не загружено.
   */
  planTerms: PlanTerms | null;
  trialProgress: TrialProgress | null;
  /**
   * WEB-4180: признак «пробный положен» из того же ответа; `null` — нет в
   * ответе (маска прав, старый сервер) или ещё не загружено — пробный не
   * обещаем.
   */
  trialOffer: TrialOffer | null;
  /**
   * WEB-3302 (сервер — WEB-2502): состояние доступа организации из того же
   * ответа `GET /billing`; `null` — сервер не прислал (старше кабинета) или
   * ответа ещё не было. Вид тарифа решает `components/billing/planView.ts`.
   */
  accessState: OrgAccessState | null;
  isLoadingPayFromBalanceToggle: boolean;
  /**
   * Ф3 (ревизия десятки 3, 15.08): requestId of the most recently DISPATCHED
   * fetchTransactionsThunk — see QuizzesState.latestRequestId (types/index.ts)
   * for the full rationale (out-of-order guard mirrored from statisticsSlice's
   * WEB-1337 P5).
   */
  transactionsLatestRequestId: string | null;
  /**
   * WEB-1518: состояние ПРОВЕРКИ истории транзакций — отдельно от данных.
   *
   * Зачем отдельное поле, а не общий `error` ниже: `error` пишут все запросы
   * среза (биллинг, checkout, портал, покупка кредитов), и любой их `pending`
   * чистит его в null. Читай таблица общий `error` — упавшее пополнение
   * рисовало бы ошибку поверх нормально загруженной истории, а чужая загрузка
   * стирала бы настоящую ошибку истории, возвращая ложное «Транзакций пока
   * нет» (список на отказе обнуляется намеренно, см. rejected ниже).
   *
   * Словарь состояний — общий канон `@/utils/checkOutcome`.
   */
  transactionsCheck: CheckState;
}

const initialState: BillingState = {
  subscription: null,
  // WEB-1290: before the first load we genuinely do not know the tier — do
  // NOT default to 'FREE' (that made a not-yet-loaded paid org look free).
  tier: 'UNKNOWN',
  limits: null,
  usage: null,
  transactions: [],
  transactionsTotal: 0,
  isLoadingBilling: false,
  isLoadingTransactions: false,
  isLoadingCheckout: false,
  isLoadingTopUp: false,
  error: null,
  redirectUrl: null,
  billingPrompt: null,
  balanceCents: 0,
  availableCents: null,
  heldCents: null,
  debtBlocked: false,
  debtCents: 0,
  lots: null,
  subscriptionOrgId: null,
  billingLoaded: false,
  walletConfirmed: false,
  costCatalog: null,
  isLoadingCostCatalog: false,
  costCatalogRequestId: null,
  costCatalogAttempted: false,
  planPrices: null,
  isLoadingPlanPrices: false,
  payFromBalanceOnCardFailEnabled: false,
  paidFromBalanceBanner: false,
  billingHidden: false,
  planTerms: null,
  trialProgress: null,
  trialOffer: null,
  accessState: null,
  isLoadingPayFromBalanceToggle: false,
  transactionsLatestRequestId: null,
  // WEB-1518: до первого запроса проверка ничего не утверждает. `idle` тут —
  // «спрашивать пока не о чем», а пустой список честно пуст.
  transactionsCheck: 'idle',
};

export const fetchBillingThunk = createAsyncThunk<
  BillingResponse,
  string | undefined,
  {
    rejectValue: ApiErrorInfo;
    // WEB-1487×WEB-1524: the org active when the request was MADE — see
    // BillingState.subscriptionOrgId doc.
    fulfilledMeta: { requestOrgId: string | null };
  }
>(
  'billing/fetchBilling',
  async (projectId, { rejectWithValue, getState, fulfillWithValue }) => {
    // WEB-1131 (деньги/права): читаем activeOrgId из стора ЗДЕСЬ, внутри
    // thunk'а, а не полагаемся на то, что каждый вызывающий передаст его
    // явно — так все существующие вызовы fetchBillingThunk()/(undefined)
    // (переключатель организаций, создание организации, виджет баланса в
    // шапке, useBillingAutoRefresh, BillingTab) автоматически становятся
    // org-scoped без правки по одной точке и без риска забытого места.
    // До первой загрузки activeOrgId (org ещё не выбрана/не подтянута)
    // остаётся undefined — прежнее поведение: сервер сам резолвит активную
    // орг/дефолтный проект вызывающего (см. billing.controller.ts:87).
    const activeOrgId =
      (getState() as { organizations?: { activeOrgId: string | null } })
        .organizations?.activeOrgId ?? undefined;
    try {
      const response = await billingApi.get(projectId, activeOrgId);
      // WEB-936 п.5: широковещает баланс прочим вкладкам того же origin —
      // см. utils/billingSync.ts (кросс-вкладочный канал независимый от
      // видимости/фокуса, дополняет useBillingAutoRefresh). WEB-1337 P6:
      // кладём activeOrgId рядом с балансом, чтобы вкладки чужих организаций
      // могли проигнорировать этот сигнал вместо зацикленного пинг-понга.
      // WEB-2863: и свободный остаток с резервом — появление резерва при той
      // же сумме порций обязано будить соседние вкладки.
      broadcastBalanceSync(response.data.balanceCents ?? 0, activeOrgId ?? null, {
        availableCents: response.data.availableCents,
        heldCents: response.data.heldCents,
      });
      // WEB-1487×WEB-1524: tag the fulfilled action's meta with the org that
      // was active WHEN THIS REQUEST WAS MADE (captured above, before the
      // await) — not whatever activeOrgId happens to be by the time this
      // resolves. See BillingState.subscriptionOrgId doc for why.
      return fulfillWithValue(response.data, { requestOrgId: activeOrgId ?? null });
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const fetchTransactionsThunk = createAsyncThunk<
  TransactionListResponse,
  TransactionQueryParams,
  { rejectValue: ApiErrorInfo }
>(
  'billing/fetchTransactions',
  async (params, { rejectWithValue, getState }) => {
    // WEB-1131: та же org-scoping дыра, что и у fetchBillingThunk выше —
    // GET /billing/transactions тоже принимает membership-checked orgId
    // (GetTransactionsQueryDto, WEB-660), но никто его не передавал.
    // Explicit params.orgId (если вызывающий уже его знает) побеждает.
    const activeOrgId =
      (getState() as { organizations?: { activeOrgId: string | null } })
        .organizations?.activeOrgId ?? undefined;
    try {
      const response = await billingApi.getTransactions({
        ...params,
        orgId: params.orgId ?? activeOrgId,
      });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export interface CreateCheckoutArgs {
  projectId: string;
  /** WEB-3302 (после WEB-2504): оформляется только PRO. */
  tier: 'PRO';
  /**
   * WEB-4008: период с карточки тарифа. Не задан — вызов прежний, двумя
   * аргументами (окно тарифов `PricingModal` его не шлёт).
   */
  period?: CheckoutPeriod;
}

export const createCheckoutThunk = createAsyncThunk<
  CheckoutResponse,
  CreateCheckoutArgs,
  { rejectValue: ApiErrorInfo }
>(
  'billing/createCheckout',
  async ({ projectId, tier, period }, { rejectWithValue }) => {
    try {
      const response =
        period === undefined
          ? await billingApi.createCheckout(projectId, tier)
          : await billingApi.createCheckout(projectId, tier, period);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const createPortalThunk = createAsyncThunk<
  PortalResponse,
  string,
  { rejectValue: ApiErrorInfo }
>(
  'billing/createPortal',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await billingApi.createPortal(projectId);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

/**
 * WEB-1094 (деньги) — owner-toggle «оплачивать тариф с баланса, если карта
 * не прошла». Default OFF; только этот путь может его включить.
 */
export const setPayFromBalanceToggleThunk = createAsyncThunk<
  { enabled: boolean },
  { projectId: string; enabled: boolean },
  { rejectValue: ApiErrorInfo }
>('billing/setPayFromBalanceToggle', async ({ projectId, enabled }, { rejectWithValue }) => {
  try {
    const response = await billingApi.setPayFromBalanceToggle(projectId, enabled);
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(extractApiError(error));
  }
});

export const createCreditsPurchaseThunk = createAsyncThunk<
  { url: string },
  CreditsPurchaseRequest,
  { rejectValue: CreditsPurchaseError }
>('billing/createCreditsPurchase', async (request, { rejectWithValue }) => {
  try {
    const response = await billingApi.createCreditsPurchase(request);
    return response.data;
  } catch (error: unknown) {
    // WEB-1954: отказ уходит на экран РАЗОБРАННЫМ. Раньше здесь собиралась
    // пара `{ status, message }` — `code`/`reasonCode` терялись, и экран
    // пополнения звал `localizeError` голым текстом, то есть мимо реестра.
    return rejectWithValue(extractApiError(error));
  }
});

/**
 * WEB-611 (money HIGH): fetches the live Pro price from Stripe so the
 * pricing UI never shows a hardcoded number that can drift from what Stripe
 * actually charges.
 */
export const fetchPlanPricesThunk = createAsyncThunk<
  BillingPlansResponse,
  void,
  { rejectValue: ApiErrorInfo }
>('billing/fetchPlanPrices', async (_, { rejectWithValue }) => {
  try {
    const response = await billingApi.getPlans();
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(extractApiError(error));
  }
});

export const fetchCostCatalogThunk = createAsyncThunk<
  Record<string, number>,
  void,
  { rejectValue: ApiErrorInfo }
>(
  'billing/fetchCostCatalog',
  async (_, { rejectWithValue }) => {
    try {
      const response = await billingApi.getCostCatalog();
      const catalog = response.data as AiCostCatalogResponse;
      const operations = Object.fromEntries(
        Object.entries(catalog.operations).map(([key, op]) => [key, op.creditsRequired]),
      );
      // WEB-830: порог капа картинок кладём в тот же плоский справочник —
      // форма генерации читает его по ключу, чтобы считать растущую оценку и
      // не хардкодить бэкендовую константу. Старый бэкенд поля не отдаёт —
      // тогда ключа просто нет, и оценка берёт свой запасной порог.
      //
      // WEB-1847: тем же способом кладём оценки СВЕРХУ крупного хода чата.
      // Ключа нет (старый бэкенд) — подпись у поля ввода не назовёт число,
      // но и не выдумает его: в деньгах молчание честнее догадки.
      const extras: Record<string, number> = {};
      if (typeof catalog.imageCapFloor === 'number') {
        extras[IMAGE_CAP_FLOOR_KEY] = catalog.imageCapFloor;
      }
      // WEB-1362 п.1: максимумы экранов — ТОТ ЖЕ провод, что у порога капа
      // картинок выше, и он был оборван здесь же. Сервер отдаёт оба числа
      // специально (cost-catalog-retail.ts), расчёт оценки уже готов читать их
      // по ключам quiz_screen_count_max / paywall_screen_count_max — но сюда
      // они не переносились, и расчёт ВСЕГДА падал на запасные 40 и 15.
      // Проверка типом обязательна: null от старого бэкенда превратил бы
      // потолок в NaN, то есть в цену, посчитанную из мусора.
      if (typeof catalog.quizScreenCountMax === 'number') {
        extras[QUIZ_SCREEN_COUNT_MAX_KEY] = catalog.quizScreenCountMax;
      }
      if (typeof catalog.paywallScreenCountMax === 'number') {
        extras[PAYWALL_SCREEN_COUNT_MAX_KEY] = catalog.paywallScreenCountMax;
      }
      if (typeof catalog.editTurnMeteredEstimateCredits === 'number') {
        extras[EDIT_TURN_METERED_ESTIMATE_KEY] =
          catalog.editTurnMeteredEstimateCredits;
      }
      if (
        typeof catalog.editTurnMeteredEstimateWithReferenceCredits === 'number'
      ) {
        extras[EDIT_TURN_METERED_ESTIMATE_WITH_REFERENCE_KEY] =
          catalog.editTurnMeteredEstimateWithReferenceCredits;
      }
      return { ...operations, ...extras };
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setBillingPrompt(state, action: { payload: BillingPrompt }) {
      return { ...state, billingPrompt: action.payload };
    },
    clearBillingPrompt(state) {
      return { ...state, billingPrompt: null };
    },
    clearBillingError(state) {
      return { ...state, error: null };
    },
    clearRedirectUrl(state) {
      return { ...state, redirectUrl: null };
    },
  },
  extraReducers: (builder) => {
    // fetchBillingThunk
    builder
      .addCase(fetchBillingThunk.pending, (state) => ({
        ...state,
        isLoadingBilling: true,
        error: null,
      }))
      .addCase(fetchBillingThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingBilling: false,
        billingLoaded: true,
        walletConfirmed: true,
        subscription: action.payload.subscription,
        // WEB-1120: null для читателя без прав на тариф (участник; админ без
        // billingAccess, WEB-1093). WEB-1290: раньше коэрсили это в 'FREE' —
        // тариф с деньгами тогда выглядел бесплатным для такого читателя.
        // 'UNKNOWN' — отдельное состояние «не знаем» (BillingTier), которое
        // НЕ должно трактоваться как «точно бесплатный» ни одним потребителем
        // (useTier и все сравнения `tier === 'FREE'` теперь безопасны для
        // 'UNKNOWN' — see types/billing.ts BillingTier doc).
        tier: action.payload.tier ?? 'UNKNOWN',
        limits: action.payload.limits,
        usage: action.payload.usage,
        balanceCents: action.payload.balanceCents ?? 0,
        // WEB-2826: ответ без полей резерва ЗАТИРАЕТ прежние числа (null =
        // «свободно всё, резерва нет»), а не держит резерв прошлого ответа.
        availableCents: action.payload.availableCents ?? null,
        heldCents: action.payload.heldCents ?? null,
        // Ревизия пачек 6-7 (п.4): устаревший ответ без полей = «долга не
        // знаем» → не блокируем (то же направление отказа, что было до правки).
        debtBlocked: action.payload.debtBlocked ?? false,
        debtCents: action.payload.debtCents ?? 0,
        lots: action.payload.lots ? [...action.payload.lots] : null,
        // WEB-1487×WEB-1524: see BillingState.subscriptionOrgId doc — tags
        // this snapshot with the org it was actually requested for.
        subscriptionOrgId: action.meta.requestOrgId ?? null,
        payFromBalanceOnCardFailEnabled:
          action.payload.payFromBalanceOnCardFailEnabled ?? false,
        paidFromBalanceBanner: action.payload.paidFromBalanceBanner ?? false,
        billingHidden: action.payload.billingHidden ?? false,
        // WEB-4008: ответ без полей затирает прежние условия (null).
        planTerms: action.payload.planTerms ?? null,
        trialProgress: action.payload.trialProgress ?? null,
        // WEB-4180: ответ без поля затирает прежний признак (null).
        trialOffer: action.payload.trialOffer ?? null,
        // WEB-3302: ответ без поля затирает прежнее значение (null — «не знаем»).
        accessState: action.payload.accessState ?? null,
      }))
      // WEB-1337 P2 — deliberately does NOT reset subscription/tier/limits/
      // usage/balanceCents/lots here (unlike quizzesSlice/domainsSlice/
      // statisticsSlice rejected cases, which DO blank their list/rows).
      // Money-gating consumers (WalletBalanceCard, PastDueBanner,
      // ThreePanelEditor.isProjectBlocked, RenewDomainPopup.hasEnoughBalance,
      // OrgBuyDomainModal) read balanceCents/tier directly and gate real
      // decisions on it WITHOUT checking `error` — they only gate on
      // `billingLoaded` (already true past the first load). Blanking
      // balanceCents to 0 / tier to FREE on a transient rejected refetch
      // would make those consumers treat "we don't know" as "confirmed
      // zero/FREE", which is a worse and actively money-risky failure mode
      // than showing the last-known-good numbers next to an error. Stale
      // org/project context is handled separately and correctly by the
      // switchActiveOrgThunk/switchActiveProjectThunk-driven centralized
      // reset (store/index.ts) which blanks this slice BEFORE any fetch for
      // the new context can land — so this rejected case is only ever
      // reached for a same-context retry, where keeping the last confirmed
      // values is the safer choice. Flagged for PM/CTO sign-off rather than
      // guessed — see WEB-1337 handoff notes.
      .addCase(fetchBillingThunk.rejected, (state, action) => ({
        ...state,
        isLoadingBilling: false,
        billingLoaded: true,
        error: rejectedApiError(action),
      }));

    // fetchTransactionsThunk
    builder
      .addCase(fetchTransactionsThunk.pending, (state, action) => ({
        ...state,
        isLoadingTransactions: true,
        error: null,
        // WEB-1518: повтор снимает прошлый отказ — иначе экран залипал бы в
        // ошибке даже после удачной загрузки.
        transactionsCheck: 'loading' as CheckState,
        // Ф3 (ревизия десятки 3, 15.08): requestId-гард — см. BillingState.transactionsLatestRequestId.
        transactionsLatestRequestId: action.meta?.requestId ?? null,
      }))
      .addCase(fetchTransactionsThunk.fulfilled, (state, action) => {
        // Ф3: a stale (superseded) response landing late must not overwrite
        // the fresher history already on screen (mirrors quizzesSlice/paywallsSlice/domainsSlice).
        if (
          state.transactionsLatestRequestId != null &&
          action.meta.requestId !== state.transactionsLatestRequestId
        ) {
          return state;
        }
        return {
          ...state,
          isLoadingTransactions: false,
          transactions: action.payload.transactions,
          transactionsTotal: action.payload.total,
          // WEB-1518: ответ сервера получен — только теперь пустой список
          // имеет право читаться как «транзакций нет».
          transactionsCheck: 'idle' as CheckState,
        };
      })
      // WEB-1337 P2: unlike fetchBillingThunk.rejected above, the
      // transaction history is a plain activity list with no gating logic
      // reading it — safe to clear alongside the error so a failed refetch
      // doesn't leave a stale (possibly previous org/project's) history
      // rendered as if current.
      .addCase(fetchTransactionsThunk.rejected, (state, action) => {
        // Ф3: same out-of-order guard as fulfilled above.
        if (
          state.transactionsLatestRequestId != null &&
          action.meta.requestId !== state.transactionsLatestRequestId
        ) {
          return state;
        }
        return {
          ...state,
          isLoadingTransactions: false,
          error: rejectedApiError(action),
          transactions: initialState.transactions,
          transactionsTotal: initialState.transactionsTotal,
          // WEB-1518: вот здесь обнулённый список и превращался в «Транзакций
          // пока нет». Теперь рядом с обнулением стоит признак провалившейся
          // проверки, и таблица проверяет его ПЕРЕД пустотой.
          // ⚠ Статус ответа сюда не доезжает: thunk отклоняет строкой
          // (rejectWithValue(error.message)), поэтому 403 практически всегда
          // классифицируется как 'error'. Для этого экрана разницы нет (обе
          // ветки — «не смогли проверить»), а классификатор оставлен общий:
          // если payload когда-нибудь начнёт нести статус, 403 станет
          // 'forbidden' сам, без правки этого места.
          transactionsCheck: checkStateFromError(
            action.payload ?? action.error,
          ) as CheckState,
        };
      });

    // createCheckoutThunk
    builder
      .addCase(createCheckoutThunk.pending, (state) => ({
        ...state,
        isLoadingCheckout: true,
        error: null,
      }))
      .addCase(createCheckoutThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingCheckout: false,
        redirectUrl: action.payload.url,
      }))
      .addCase(createCheckoutThunk.rejected, (state, action) => ({
        ...state,
        isLoadingCheckout: false,
        error: rejectedApiError(action),
      }));

    // createPortalThunk
    builder
      .addCase(createPortalThunk.pending, (state) => ({
        ...state,
        isLoadingCheckout: true,
        error: null,
      }))
      .addCase(createPortalThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingCheckout: false,
        redirectUrl: action.payload.url,
      }))
      .addCase(createPortalThunk.rejected, (state, action) => ({
        ...state,
        isLoadingCheckout: false,
        error: rejectedApiError(action),
      }));

    // setPayFromBalanceToggleThunk (WEB-1094)
    builder
      .addCase(setPayFromBalanceToggleThunk.pending, (state) => ({
        ...state,
        isLoadingPayFromBalanceToggle: true,
        error: null,
      }))
      .addCase(setPayFromBalanceToggleThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingPayFromBalanceToggle: false,
        payFromBalanceOnCardFailEnabled: action.payload.enabled,
      }))
      .addCase(setPayFromBalanceToggleThunk.rejected, (state, action) => ({
        ...state,
        isLoadingPayFromBalanceToggle: false,
        error: rejectedApiError(action),
      }));

    // createCreditsPurchaseThunk (top-up)
    builder
      .addCase(createCreditsPurchaseThunk.pending, (state) => ({
        ...state,
        isLoadingTopUp: true,
        error: null,
      }))
      .addCase(createCreditsPurchaseThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingTopUp: false,
        redirectUrl: action.payload.url,
      }))
      .addCase(createCreditsPurchaseThunk.rejected, (state, action) => ({
        ...state,
        isLoadingTopUp: false,
        error: rejectedApiError(action),
      }));

    // fetchPlanPricesThunk (WEB-611)
    builder
      .addCase(fetchPlanPricesThunk.pending, (state) => ({
        ...state,
        isLoadingPlanPrices: true,
      }))
      .addCase(fetchPlanPricesThunk.fulfilled, (state, action) => ({
        ...state,
        isLoadingPlanPrices: false,
        planPrices: action.payload,
      }))
      .addCase(fetchPlanPricesThunk.rejected, (state, action) => ({
        ...state,
        isLoadingPlanPrices: false,
        error: rejectedApiError(action),
      }));

    // fetchCostCatalogThunk
    builder
      .addCase(fetchCostCatalogThunk.pending, (state, action) => ({
        ...state,
        isLoadingCostCatalog: true,
        costCatalogRequestId: action.meta.requestId,
        costCatalogAttempted: true,
      }))
      // WEB-3911: чужой ответ (не последнего запроса или после сброса
      // контекста) — не ложится, состояние как есть.
      .addCase(fetchCostCatalogThunk.fulfilled, (state, action) =>
        action.meta.requestId !== state.costCatalogRequestId
          ? state
          : {
              ...state,
              isLoadingCostCatalog: false,
              costCatalogRequestId: null,
              costCatalog: action.payload,
            },
      )
      .addCase(fetchCostCatalogThunk.rejected, (state, action) =>
        action.meta.requestId !== state.costCatalogRequestId
          ? state
          : {
              ...state,
              isLoadingCostCatalog: false,
              costCatalogRequestId: null,
              error: rejectedApiError(action),
            },
      );
  },
});

export const {
  setBillingPrompt,
  clearBillingPrompt,
  clearBillingError,
  clearRedirectUrl,
} = billingSlice.actions;

export default billingSlice.reducer;
